// src/pages/assistants.tsx

import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
    Plus, 
    Bot,
    Lock,
    ShieldAlert,
    Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
// import { Separator } from '@/components/ui/separator';
import { useNavigate } from 'react-router-dom';
import { getAssistants } from '@/api/assistants/assistants.api';
import type { Assistant } from '@/api/assistants/assistants.types';
// import { Templates } from './components/templates-section';
import { AssistantSkeleton, AssistantCard } from './components/AssistantCard';
import { useAuth } from '@/context/AuthContext';
import { 
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { EmptyState } from '@/components/ui/empty-state';
import { SiteHeader } from '@/components/site-header';
import { containerVariants } from '@/components/animations';
import { Combobox } from '@/components/ui/combobox';

const Assistants = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [assistants, setAssistants] = useState<Assistant[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [languageFilter, setLanguageFilter] = useState<string>('all');
    const { user } = useAuth();

    const canCreateAssistant = user?.permissions?.assistant === 'EDITOR';

    useEffect(() => {
        const fetchAssistants = async () => {
            setIsLoading(true);
            try {
                const response = await getAssistants();
                if (response.error) {
                    setError(response.error);
                } else if (response.data) {
                    setAssistants(response.data);
                }
            } catch (error: unknown) {
                setError('Failed to fetch assistants');
            } finally {
                setIsLoading(false);
            }
        };

        fetchAssistants();
    }, []);

    const handleNewAssistant = () => {
        if (canCreateAssistant) {
            navigate('/dashboard/assistants/new');
        }
    };

    // Get unique languages from assistants
    const languageOptions = useMemo(() => {
        const languages = new Set<string>();
        assistants.forEach(assistant => {
            if (assistant.transcriber?.language) {
                languages.add(assistant.transcriber.language);
            }
        });
        
        return [
            { value: 'all', label: 'All Languages' },
            ...Array.from(languages)
                .sort()
                .map(lang => ({
                    value: lang,
                    label: lang.toUpperCase()
                }))
        ];
    }, [assistants]);

    // Filter assistants based on search query and language
    const filteredAssistants = useMemo(() => {
        let filtered = assistants;
        
        // Apply language filter
        if (languageFilter !== 'all') {
            filtered = filtered.filter(assistant => 
                assistant.transcriber?.language === languageFilter
            );
        }
        
        // Apply search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(assistant => 
                assistant.name.toLowerCase().includes(query) ||
                assistant.id.toLowerCase().includes(query) ||
                assistant.voice?.voiceId?.toLowerCase().includes(query) ||
                assistant.transcriber?.language?.toLowerCase().includes(query)
            );
        }
        
        return filtered;
    }, [assistants, searchQuery, languageFilter]);

    const NewAssistantButton = ({ size = "sm", className = "" }: { size?: "sm" | "default" | "lg" | "icon", className?: string }) => (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div>
                        <Button 
                            onClick={handleNewAssistant} 
                            size={size} 
                            className={className}
                            disabled={!canCreateAssistant}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            New Assistant
                            {!canCreateAssistant && <Lock className="w-3.5 h-3.5 ml-2 opacity-70" />}
                        </Button>
                    </div>
                </TooltipTrigger>
                {!canCreateAssistant && (
                    <TooltipContent className="flex items-center gap-2 max-w-xs">
                        <ShieldAlert className="w-12 h-12 text-amber-500" />
                        <p>You need editor permissions to create new assistants. Contact your administrator for access.</p>
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );

    return (
        <>
            <SiteHeader 
                title="Assistants"
                subtitle="Manage your AI voice assistants and their configurations."
            >
                {!isLoading && assistants.length > 0 && (
                    <>
                        {/* Search Input */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <Input
                                placeholder="Search assistants..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-9 w-[250px] lg:w-[300px] pl-9"
                            />
                        </div>

                        {/* Language Filter */}
                        <Combobox
                            options={languageOptions}
                            value={languageFilter}
                            onValueChange={(value) => setLanguageFilter(value || 'all')}
                            placeholder="All Languages"
                            searchPlaceholder="Search languages..."
                            emptyMessage="No language found."
                            className="w-[170px]"
                        />

                        {/* New Assistant Button */}
                        <NewAssistantButton size="sm" />
                    </>
                )}
            </SiteHeader>

            <div className="flex-1 overflow-auto">
                {error && (
                    <div className="px-4 py-6 md:px-8 md:py-8">
                        <div className="text-red-500 text-sm">
                            {error}
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <motion.div
                            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {[...Array(6)].map((_, i) => (
                                <AssistantSkeleton key={i} />
                            ))}
                        </motion.div>
                    </motion.div>
                ) : assistants.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="w-full -mt-12"
                        >
                            <EmptyState
                                icon={Bot}
                                title="No Assistants Yet"
                                description="Create your first AI voice assistant to start automating conversations and providing intelligent support."
                                action={{
                                    label: "Create Assistant",
                                    onClick: handleNewAssistant,
                                    icon: Plus,
                                    disabled: !canCreateAssistant,
                                    disabledReason: canCreateAssistant ? undefined : "You need editor permissions to create assistants. Contact your administrator for access."
                                }}
                            />
                        </motion.div>
                    </div>
                ) : filteredAssistants.length === 0 ? (
                    <div className="h-full flex items-center justify-center -mt-12">
                        <div className="text-center">
                            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                <Search className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">No Matching Assistants</h3>
                            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                                {searchQuery && languageFilter !== 'all' 
                                    ? `No assistants found matching your search and language filter.`
                                    : searchQuery 
                                    ? `No assistants found matching "${searchQuery}".`
                                    : `No assistants found for the selected language.`
                                }
                            </p>
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                    setSearchQuery('');
                                    setLanguageFilter('all');
                                }}
                            >
                                Clear All Filters
                            </Button>
                        </div>
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <motion.div
                            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {filteredAssistants.map((assistant) => (
                                <AssistantCard key={assistant.id} assistant={assistant} />
                            ))}
                        </motion.div>
                    </motion.div>
                )}
            </div>
        </>
    );
};

export default Assistants;
